const STARTS_WITH_LETTER_OR_DIGIT = /^[\p{L}\p{N}]/u

/**
 * The words of a name that can give an initial. A leading emoji or symbol
 * ("🧪 TEST: Acme Corp") is decoration, so words starting with a letter or
 * digit win; a name made only of symbols keeps all of its words.
 */
export function getInitialWords(name: string): string[] {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const lettered = words.filter(word => STARTS_WITH_LETTER_OR_DIGIT.test(word))
  return lettered.length > 0 ? lettered : words
}

/**
 * The first `count` characters as whole code points: indexing a string
 * (`word[0]`, `charAt`, `substring`) splits an emoji into half a surrogate
 * pair, which renders as "�".
 */
export function leadingChars(word: string, count = 1): string {
  return Array.from(word).slice(0, count).join('')
}

interface InitialsOptions {
  /** How many letters a one-word name gives: "Acme" → "A", or "AC" with 2. */
  singleWordLetters?: 1 | 2
}

/**
 * Get user initials from a full name string
 * @param name - The full name string
 * @returns The initials as a string (e.g., "John Doe" -> "JD")
 */
export function getInitials(
  name: string | null | undefined,
  { singleWordLetters = 1 }: InitialsOptions = {},
): string {
  if (!name || typeof name !== 'string') {
    return ''
  }

  const words = getInitialWords(name)
  if (words.length === 1) {
    return leadingChars(words[0], singleWordLetters).toUpperCase()
  }

  // Limit to 2 characters for better display
  return leadingChars(
    words.map(word => leadingChars(word).toUpperCase()).join(''),
    2,
  )
}

/** One initial for a single-letter badge: "🧪 Acme" → "A", "ada" → "A". */
export function getInitial(name: string | null | undefined): string {
  return leadingChars(getInitials(name))
}

/**
 * Initials from separate first and last name fields: ("Ada", "Lovelace") →
 * "AL"; either may be missing.
 */
export function getFirstLastInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return `${getInitial(firstName)}${getInitial(lastName)}`
}
